import React, { FC, useRef, useState, useMemo } from 'react'
import Alert from '@mui/material/Alert'
import { Flex, Spin } from 'antd'
import { selectCurrentZone } from '../../store/global'
import NavigationHeader from 'components/NavigationHeader/NavigationHeader'
import BusinessesSummary from 'components/BusinessesSummary/BusinessesSummary'
import { BaseTableOverview, CurrentZone } from 'utils/types'
import { EcommerceEntityForTable } from 'containers/EcommerceTablePage/types'
import { useVerticalOverviewQuery } from 'hooks/useVerticalOverviewQuery'
import { useVerticalEntitiesQuery } from 'hooks/useVerticalEntitiesQuery'
import useVerticalSearchAndSort from 'hooks/useVerticalSearchAndSort'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import cx from 'classnames'
import messages from './messages'
import { useEmptyColumnsQuery } from 'hooks/useEmptyColumnsQuery'
import Search from 'UI/Search/Search'
import ExportCSVTableVertical from 'components/ExportCSVTableVertical/ExportCSVTableVertical'
import { COLUMNS_IDS } from 'UI/VerticalTable/utils'
import { ecommerceColumns, keysForEmptyColumnsRequest } from './utils'
import VerticalTable from 'UI/VerticalTable/VerticalTable'
import { PAGE_SIZE } from 'components/verticals/constants'
import styles from 'styles/TablePageCommon.scss'
import NoDataContainer from 'components/NoDataContainer'

const EcommerceTablePage: FC = () => {
  const currentZone: CurrentZone = useSelector(selectCurrentZone)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const statisticsActivityRef = useRef<HTMLDivElement | null>(null)
  const navigationRef = useRef<HTMLDivElement | null>(null)

  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const { searchValue, searchValueDebounced, sorting, setSearchValue, setSorting } = useVerticalSearchAndSort()

  const { data: emptyColumnsData, isFetched: emptyColumnsIsFetched } = useEmptyColumnsQuery({
    verticalIndices: currentZone?.ecommerceIndices,
    currentQueryKey: 'ecommerce-entities-key',
    keys: keysForEmptyColumnsRequest,
  })

  const { data: overviewData, isLoading: isLoadingOverview } = useVerticalOverviewQuery<BaseTableOverview>({
    verticalIndices: currentZone?.ecommerceIndices,
    currentQueryKey: 'ecommerce-overview-key',
    searchValue: searchValueDebounced,
  })

  const { verticalEntities, fetchNextPage, isFetching, isLoading, isError } =
    useVerticalEntitiesQuery<EcommerceEntityForTable>({
      verticalIndices: currentZone?.ecommerceIndices,
      currentQueryKey: 'ecommerce-entities-key',
      searchValue: searchValueDebounced,
      sorting,
      pageSize: PAGE_SIZE,
    })

  const hasNoData = useMemo(() => {
    return !isLoading && !isError && (!verticalEntities || verticalEntities.length === 0) && !searchValueDebounced
  }, [isLoading, isError, verticalEntities, searchValueDebounced])

  return (
    <div ref={containerRef} className={styles.container}>
      <div ref={navigationRef}>
        <nav id='navigationHeaderWrapper' className={styles.navigationHeaderWrapper}>
          <NavigationHeader />
        </nav>
        <header className='page-header withNavigationHeader'>
          <h1 className={cx('page-title', 'h1')}>
            <FormattedMessage {...messages.header} />
          </h1>
        </header>
      </div>
      {hasNoData ? (
        <NoDataContainer verticalName="Ecommerce" zoneName={currentZone?.name} />
      ) : (
        <>
          <section ref={statisticsActivityRef} className={styles.summary} id={'businessSummary'}>
            <BusinessesSummary
              isLoading={isLoadingOverview}
              summary={{
                entityCounts: overviewData?.summary.entitiesCount,
                incomeCounts: overviewData?.summary.incomeCounts,
                topIndustries: overviewData?.summary.platforms.map(({ source, count }) => [source, count]),
                industriesCount: overviewData?.summary.platforms.reduce((acc, { count }) => acc + count, 0),
              }}
              isInfluencers
            />
          </section>
          {isLoading ? (
            <Flex align='center' gap='middle' justify='center' style={{ padding: '45px 14px' }}>
              <Spin size='large' />
            </Flex>
          ) : isError ? (
            <Alert severity='error' sx={{ fontFamily: 'Source Sans Pro', fontSize: '1rem', alignItems: 'center' }}>
              Something went wrong
            </Alert>
          ) : (
            <>
              {emptyColumnsIsFetched && (
                <VerticalTable
                  tableId="ecommerce"
                  pageSize={PAGE_SIZE}
                  enableColumnPicker
                  dataSource={verticalEntities as any[]}
                  columns={ecommerceColumns}
                  containerRef={containerRef}
                  additionalRefs={[statisticsActivityRef, navigationRef]}
                  fetchNextPage={fetchNextPage}
                  total={overviewData?.total || 0}
                  loading={isFetching}
                  disabledColumns={emptyColumnsData}
                  onSelectionChange={setSelectedIds}
                  removeFromPickerColumns={[COLUMNS_IDS.verifiedName]}
                  rowClassName={styles.tableRow}
                  onRow={record => {
                    return {
                      onClick: () => {
                        window.open(`${window.location.origin}/vertical/ecommerce/${record.id}`, '_blank')
                      },
                    }
                  }}
                  onChange={(pagination, filters, sorter) => {
                    if (!Array.isArray(sorter)) {
                      if (sorter.order) {
                        setSorting([{ id: sorter.columnKey as string, desc: sorter.order === 'descend' }])
                      } else {
                        setSorting([])
                      }
                    }
                  }}
                >
                  <div className={styles.counter}>
                    {`Ecommerce: `}
                    <span>{selectedIds.length > 0 ? `${selectedIds.length} selected` : overviewData?.total}</span>
                  </div>
                  <Search
                    placeholder='input search text'
                    enterButton
                    allowClear
                    style={{ width: 264 }}
                    loading={isFetching}
                    onSearch={newSearchValue => setSearchValue(newSearchValue)}
                  />
                  <ExportCSVTableVertical
                    zoneIndex={currentZone?.ecommerceIndices}
                    csvFileName='Ecommerce'
                    searchValue={searchValue}
                    selectedIds={selectedIds}
                    sorting={sorting}
                    total={overviewData?.total}
                  />
                </VerticalTable>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

export default EcommerceTablePage
